import ISignupUser from '@/app/(auth)/signup/types/ISignupUser';
import { createContext, useContext, useMemo } from 'react';
import type { SignupStepId } from '@/app/(auth)/signup/SIGNUP_STEPS';

export interface ISignupStepContext {
  stepId: SignupStepId;

  next?: (extras?: Record<string, string>) => void;
  prev?: () => void;
  details: ISignupUser;
  setDetails: (details: ISignupUser) => void;
  isSubmittingNext: boolean;
  isSubmittingPrev: boolean;
  isPresent: boolean;
  isValid: boolean;
  isPassport: boolean;
  fieldValidity: {
    [field: string]: boolean;
  };
  redirectConfig: {
    baseUrl: string;
    nexudusRedirectUrl: string;
  };
  /**
   * Provides a top-level error message such as for a failed network request
   */
  error?: string;

  /**
   * Provides a list of errors for each field. If a field has no errors it will not be present in the map.
   */
  fieldErrors: {
    [field: string]: string[];
  };
}

export const SignupStepContext = createContext<ISignupStepContext>({} as ISignupStepContext);

export const useSignupStep = () => {
  return useContext(SignupStepContext);
};

export const useNextSignupStep = () => {
  const { next, isSubmittingNext } = useSignupStep();

  const isVisible = next !== undefined;
  const handleSubmit: (event: React.FormEvent) => void = event => {
    event.preventDefault();
    next?.();
  };

  return useMemo(() => {
    return {
      isVisible,
      handleSubmit,
      isSubmitting: isSubmittingNext,
      next,
    };
  }, [isVisible, isSubmittingNext]);
};

export const usePreviousSignupStep = () => {
  const { prev, isSubmittingNext } = useSignupStep();

  const isVisible = prev !== undefined;
  const handleSubmit: (event: React.FormEvent) => void = event => {
    event.preventDefault();
    prev?.();
  };

  return useMemo(() => {
    return {
      isVisible,
      handleSubmit,
      isSubmitting: isSubmittingNext,
      prev,
    };
  }, [isVisible, isSubmittingNext]);
};

export { enableSetHomeLocation } from '@/app/(auth)/signup/components/signupStep/signupFlags';
